"use client";

import { useEffect, useState, useTransition } from "react";
import { OpenButton } from "./overlays";

// Small client pieces shared by the progression pages (event, EXP cases, pass, piggy bank, affiliate, payments).

function useNow() {
  const [now, setNow] = useState(0); // 0 until mounted: no server/client clock mismatch
  useEffect(() => {
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}
const pad = (n: number) => String(Math.floor(n)).padStart(2, "0");
export const hms = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${pad(s / 3600)}:${pad((s % 3600) / 60)}:${pad(s % 60)}`;
};

/** Big days/hrs/min/sec boxes. */
export function Countdown({ to, labels, ended }: { to: number; labels: [string, string, string, string]; ended: string }) {
  const now = useNow();
  const left = Math.max(0, to - now);
  if (now && left === 0) return <p className="font-display text-sm font-extrabold uppercase text-muted">{ended}</p>;
  const parts = [left / 864e5, (left % 864e5) / 36e5, (left % 36e5) / 6e4, (left % 6e4) / 1e3];
  return (
    <div className="flex items-center gap-1.5" suppressHydrationWarning>
      {parts.map((v, k) => (
        <div key={k} className="countdown-box">
          <span className="font-display text-xl font-extrabold tnum leading-none">{now ? pad(v) : "--"}</span>
          <span className="text-[10px] font-bold uppercase text-muted">{labels[k]}</span>
        </div>
      ))}
    </div>
  );
}

/** "Ready" or "Next in hh:mm:ss". */
export function CooldownText({ readyAt, ready, prefix, className = "" }: { readyAt: number; ready: string; prefix: string; className?: string }) {
  const now = useNow();
  if (now === 0) return <span className={className}>…</span>;
  return readyAt > now
    ? <span className={`tnum text-muted ${className}`}>{prefix} {hms(readyAt - now)}</span>
    : <span className={`text-money ${className}`}>{ready}</span>;
}

type Result = { ok?: string; error?: string };
/** Calls a (bound) server action; shows its localized ok/error message under the button. */
export function ActionButton({ action, label, className = "", disabled, onOk }: {
  action: () => Promise<Result>; label: React.ReactNode; className?: string; disabled?: boolean; onOk?: () => void;
}) {
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [pending, start] = useTransition();
  return (
    <div className="flex flex-col items-start gap-2">
      <button type="button" disabled={disabled || pending} className={className}
        onClick={() => start(async () => {
          const r = await action();
          setMsg(r.error ? { ok: false, text: r.error } : { ok: true, text: r.ok ?? "" });
          if (!r.error) onOk?.();
        })}>
        {pending ? "…" : label}
      </button>
      {msg?.text && <p className={`outcome-pop text-sm font-semibold ${msg.ok ? "text-money" : "text-danger"}`} role="status">{msg.text}</p>}
    </div>
  );
}

export function CopyField({ value, copy, copied, origin = false }: { value: string; copy: string; copied: string; origin?: boolean }) {
  const [full, setFull] = useState(value);
  const [done, setDone] = useState(false);
  useEffect(() => { if (origin) setFull(location.origin + value); }, [origin, value]);
  return (
    <div className="flex gap-2">
      <input readOnly value={full} onFocus={(e) => e.target.select()} className="field min-w-0 flex-1 px-3 py-2.5 text-sm" />
      <button type="button" className="btn btn-ghost shrink-0 px-4 text-xs"
        onClick={async () => {
          try { await navigator.clipboard.writeText(full); setDone(true); setTimeout(() => setDone(false), 1600); } catch {}
        }}>
        {done ? copied : copy}
      </button>
    </div>
  );
}

export function LoginPrompt({ text, button }: { text: string; button: string }) {
  return (
    <div className="card flex flex-col items-center gap-3 p-8 text-center">
      <p className="text-sm text-muted">{text}</p>
      <OpenButton what="login" className="btn btn-accent h-11 px-6 text-sm">{button} <span className="opacity-70">18+</span></OpenButton>
    </div>
  );
}

export function PageHead({ kicker, title, desc, children }: { kicker: string; title: string; desc: string; children?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
      <div className="min-w-0 max-w-2xl">
        <p className="kicker !text-accent">{kicker}</p>
        <h1 className="mt-1 font-display text-2xl font-extrabold uppercase sm:text-3xl">{title}</h1>
        <p className="mt-2 text-sm text-muted text-pretty">{desc}</p>
      </div>
      {children && <div className="sm:ml-auto">{children}</div>}
    </div>
  );
}
